import { FormEvent, useEffect, useMemo, useState } from "react";
import { InstructionWordLimit, instructionsExceedLimit } from "@/components/form/InstructionWordLimit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { upsertDraftLegalRole } from "@/lib/api/drafts";
import { pageDescriptionMobileHiddenClassName } from "@/lib/page-action-button";

export interface DraftLegalRoleSheetDraft {
  mode: "create" | "edit" | "customize";
  id?: string;
  name: string;
  role_key: string;
  instructions: string;
}

interface DraftLegalRoleSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draft: DraftLegalRoleSheetDraft | null;
  onSaved?: () => void;
}

export function DraftLegalRoleSheet({
  open,
  onOpenChange,
  draft,
  onSaved,
}: DraftLegalRoleSheetProps) {
  const [name, setName] = useState("");
  const [roleKey, setRoleKey] = useState("");
  const [instructions, setInstructions] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const overLimit = instructionsExceedLimit(instructions);

  const title = useMemo(() => {
    if (!draft) return "Legal role";
    if (draft.mode === "create") return "New legal role";
    if (draft.mode === "edit") return "Edit legal role";
    return "Customize legal role";
  }, [draft]);

  useEffect(() => {
    if (!open || !draft) return;
    setName(draft.name);
    setRoleKey(draft.role_key);
    setInstructions(draft.instructions);
    setSaveError(null);
  }, [open, draft]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!draft || !name.trim() || !roleKey.trim() || !instructions.trim() || overLimit) return;

    setIsSaving(true);
    setSaveError(null);
    try {
      await upsertDraftLegalRole({
        id: draft.mode === "edit" ? draft.id : undefined,
        name: name.trim(),
        role_key: roleKey.trim(),
        instructions: instructions.trim(),
      });
      onSaved?.();
      onOpenChange(false);
    } catch (reason) {
      console.error(reason);
      setSaveError(reason instanceof Error ? reason.message : "Failed to save legal role");
    } finally {
      setIsSaving(false);
    }
  }

  const lockRoleKey = draft?.mode === "customize";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="p-0">
        <form onSubmit={handleSubmit} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription className={pageDescriptionMobileHiddenClassName}>
              Instructions shape how the drafting counsel agent writes and reviews clauses for this role.
            </SheetDescription>
          </SheetHeader>

          <SheetBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="dlr-name">Role display name</Label>
              <Input
                id="dlr-name"
                placeholder="Corporate Counsel"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dlr-key">Role key</Label>
              <Input
                id="dlr-key"
                placeholder="corporate-counsel"
                value={roleKey}
                onChange={(e) => setRoleKey(e.target.value)}
                disabled={lockRoleKey}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dlr-instructions">Role instructions</Label>
              <Textarea
                id="dlr-instructions"
                rows={10}
                placeholder="Instructions that shape AI drafting posture and emphasis"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                required
              />
              <InstructionWordLimit
                text={instructions}
                kind="draft-role"
                disabled={isSaving}
                onSummarized={setInstructions}
              />
              {saveError ? (
                <p className="text-xs text-[var(--tint-red-fg)] dark:text-red-400">{saveError}</p>
              ) : null}
            </div>
          </SheetBody>

          <SheetFooter>
            <Button type="button" variant="surface" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving || overLimit || !instructions.trim()}>
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
